import { all, takeEvery, takeLatest, put, call, select } from 'redux-saga/effects';
import queryString from 'query-string';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import renderNotification from '../../helpers/notifications/renderNotification';
import { selectRisksActiveFilters } from '../filters/selectors';
import { selectRisk } from '../risk/selectors';
import { getRiskOrganizations } from '../risk/slice';
import { onGetRisks } from '../risks/api';
import { reloadRisks } from '../risks/slice';
import { onGetOrganizationsToRisks, onCreateOrganizationsToRisks, onDeleteOrganizationsToRisks } from './api';
import {
  getOrganizationsToRisks,
  getOrganizationsToRisksSucceeded,
  getOrganizationsToRisksFailed,
  bulkAddOrganizationsToRisks,
  bulkAddOrganizationsToRisksSucceeded,
  bulkAddOrganizationsToRisksFailed,
  bulkRemoveOrganizationsToRisks,
  bulkRemoveOrganizationsToRisksSucceeded,
  bulkRemoveOrganizationsToRisksFailed,
  createOrganizationsToRisks,
  createOrganizationsToRisksSucceeded,
  createOrganizationsToRisksFailed,
  updateOrganizationsToRisks,
  updateOrganizationsToRisksSucceeded,
  updateOrganizationsToRisksFailed
} from './slice';

const { risks_bulk_actions_add_organizations_success, risks_bulk_actions_remove_organizations_success } =
  NOTIFICATION_MESSAGES;

export function* getOrganizationsToRisksWorker({ payload }) {
  const { riskId } = payload;

  try {
    const response = yield call(onGetOrganizationsToRisks, { risk_id: riskId });
    yield put(getOrganizationsToRisksSucceeded(response.data));
  } catch {
    yield put(getOrganizationsToRisksFailed());
  }
}

export function* bulkAddOrganizationsToRisksWorker({ payload }) {
  const { organizationIds, callback } = payload;

  try {
    const risksFilters = yield select(selectRisksActiveFilters);
    const params = queryString.parse(risksFilters);
    params.paging = false;
    const risksResponse = yield call(onGetRisks, params);

    const riskIds = risksResponse.data.map((risk) => risk.id);

    const orgsToRisksResponse = yield call(onGetOrganizationsToRisks, {
      organization_id: organizationIds
    });

    const existingPairs = new Set(
      orgsToRisksResponse.data
        .filter((orgToRisk) => riskIds.includes(orgToRisk.risk_id))
        .map((orgToRisk) => `${orgToRisk.organization_id}-${orgToRisk.risk_id}`)
    );

    const newPairs = [];
    organizationIds.forEach((organization_id) => {
      riskIds.forEach((risk_id) => {
        if (!existingPairs.has(`${organization_id}-${risk_id}`)) {
          newPairs.push({ organization_id, risk_id });
        }
      });
    });

    if (newPairs.length > 0) {
      yield call(onCreateOrganizationsToRisks, newPairs);
    }

    yield put(bulkAddOrganizationsToRisksSucceeded());
    yield put(reloadRisks());
    callback?.();

    const message = risks_bulk_actions_add_organizations_success;

    renderNotification({
      type: 'success',
      message
    });
  } catch {
    yield put(bulkAddOrganizationsToRisksFailed());
  }
}

export function* bulkRemoveOrganizationsToRisksWorker({ payload }) {
  const { organizationIds, callback } = payload;

  try {
    const risksFilters = yield select(selectRisksActiveFilters);
    const params = queryString.parse(risksFilters);
    params.paging = false;
    const risksResponse = yield call(onGetRisks, params);

    const riskIds = risksResponse.data.map((risk) => risk.id);

    const orgsToRisksResponse = yield call(onGetOrganizationsToRisks, {
      organization_id: organizationIds
    });

    const orgsToRisksIdsToDelete = orgsToRisksResponse.data
      .filter((orgToRisk) => riskIds.includes(orgToRisk.risk_id))
      .map((orgToRisk) => orgToRisk.id);

    if (orgsToRisksIdsToDelete.length > 0) {
      yield call(onDeleteOrganizationsToRisks, orgsToRisksIdsToDelete);
    }

    yield put(bulkRemoveOrganizationsToRisksSucceeded());
    yield put(reloadRisks());
    callback?.();

    const message = risks_bulk_actions_remove_organizations_success;

    renderNotification({
      type: 'success',
      message
    });
  } catch {
    yield put(bulkRemoveOrganizationsToRisksFailed());
  }
}

export function* createOrganizationsToRisksWorker({ payload }) {
  const { organizationsToRisks } = payload;

  try {
    yield call(onCreateOrganizationsToRisks, organizationsToRisks);
    yield put(createOrganizationsToRisksSucceeded());
  } catch {
    yield put(createOrganizationsToRisksFailed());
  }
}

export function* updateOrganizationsToRisksWorker({ payload }) {
  const { organizationsToRisksToAdd, organizationToRiskIdsToDelete, callback } = payload;
  try {
    if (organizationsToRisksToAdd?.length > 0) {
      yield call(onCreateOrganizationsToRisks, organizationsToRisksToAdd);
    }
    if (organizationToRiskIdsToDelete?.length > 0) {
      yield call(onDeleteOrganizationsToRisks, organizationToRiskIdsToDelete);
    }

    const risk = yield select(selectRisk);
    yield put(updateOrganizationsToRisksSucceeded());

    if (risk?.id) {
      yield put(getRiskOrganizations({ riskId: risk.id }));
    }

    callback?.();
  } catch {
    yield put(updateOrganizationsToRisksFailed());
  }
}

export default function* policiesOrganizationsToRisksSaga() {
  yield all([
    takeLatest(getOrganizationsToRisks.type, getOrganizationsToRisksWorker),
    takeEvery(bulkAddOrganizationsToRisks.type, bulkAddOrganizationsToRisksWorker),
    takeEvery(bulkRemoveOrganizationsToRisks.type, bulkRemoveOrganizationsToRisksWorker),
    takeLatest(createOrganizationsToRisks.type, createOrganizationsToRisksWorker),
    takeLatest(updateOrganizationsToRisks.type, updateOrganizationsToRisksWorker)
  ]);
}
